import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const TicketSelectionModal = ({ isOpen, onClose, eventId, eventDetails }) => {
  const [tickets, setTickets] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && eventId) {
      fetchTickets();
    }
  }, [isOpen, eventId]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`https://event-li-backend.onrender.com/booking/${eventId}/tickets`);
      setTickets(response.data);
      
      // Initialize selected tickets
      const initialSelectedTickets = {};
      response.data.forEach(ticket => {
        initialSelectedTickets[ticket._id] = 0;
      });
      setSelectedTickets(initialSelectedTickets);
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load tickets. Please try again.');
      setLoading(false);
    }
  };

  useEffect(() => {
    // Calculate total whenever selectedTickets changes
    let newTotal = 0;
    tickets.forEach(ticket => {
      const quantity = selectedTickets[ticket._id] || 0;
      newTotal += quantity * parseFloat(ticket.price.replace(/[^\d.-]/g, ''));
    });
    setTotal(newTotal);
  }, [selectedTickets, tickets]);

  const incrementTicket = (ticketId) => {
    const ticket = tickets.find(t => t._id === ticketId);
    if (ticket && selectedTickets[ticketId] < ticket.quantity) {
      setSelectedTickets({
        ...selectedTickets,
        [ticketId]: (selectedTickets[ticketId] || 0) + 1
      });
    }
  };

  const decrementTicket = (ticketId) => {
    if (selectedTickets[ticketId] > 0) {
      setSelectedTickets({
        ...selectedTickets,
        [ticketId]: selectedTickets[ticketId] - 1
      });
    }
  };

  const handleProceed = () => {
    // Check if at least one ticket is selected
    const totalTickets = Object.values(selectedTickets).reduce((sum, qty) => sum + qty, 0);
    
    if (totalTickets === 0) {
      setError('Please select at least one ticket to continue.');
      return;
    }

    // Prepare ticket data for checkout
    const ticketSelections = tickets
      .filter(ticket => selectedTickets[ticket._id] > 0)
      .map(ticket => ({
        id: ticket._id,
        name: ticket.name,
        price: ticket.price,
        quantity: selectedTickets[ticket._id],
        type: ticket.type
      }));

    // Navigate to checkout with selected tickets
    navigate(`/single-event/checkout/${eventId}`, {
      state: {
        ticketSelections,
        eventDetails,
        total
      }
    });
    
    // Close the modal
    onClose();
  };

  const modalVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", damping: 25, stiffness: 300 } },
    exit: { opacity: 0, y: 50 }
  };

  const ticketVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  const formatPrice = (price) => {
    if (!price) return 'Free';
    // Remove any non-numeric characters except for the decimal point
    const numericPrice = parseFloat(price.replace(/[^\d.-]/g, ''));
    return numericPrice === 0 ? 'Free' : `₦${numericPrice.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <motion.div
            className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-xl"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="flex justify-between items-center border-b border-gray-200 p-6">
              <h2 className="text-2xl font-bold text-gray-800">Select Tickets</h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-700">Ticket Types</h3>
                <span className="text-lg font-medium text-gray-700">Quantity</span>
              </div>

              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : error ? (
                <div className="text-red-500 text-center py-4">{error}</div>
              ) : tickets.length === 0 ? (
                <div className="text-gray-500 text-center py-4">No tickets available for this event.</div>
              ) : (
                <div className="space-y-4">
                  {tickets.map((ticket, index) => (
                    <motion.div
                      key={ticket._id}
                      className="border border-gray-200 rounded-xl p-5 bg-white"
                      variants={ticketVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="flex justify-between items-center">
                        <div className="space-y-1">
                          <h4 className="font-semibold text-gray-800">{ticket.name}</h4>
                          <p className="text-lg font-medium text-gray-700">{formatPrice(ticket.price)}</p>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500">
                              Sales end on {formatDate(ticket.salesEnd)}
                            </span>
                          </div>
                          {ticket.description && (
                            <p className="text-sm text-gray-500 mt-1">{ticket.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => decrementTicket(ticket._id)}
                            className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                            disabled={selectedTickets[ticket._id] <= 0}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                          </button>
                          <span className="w-8 text-center">{selectedTickets[ticket._id] || 0}</span>
                          <button
                            onClick={() => incrementTicket(ticket._id)}
                            className="w-8 h-8 rounded-md bg-yellow-400 flex items-center justify-center hover:bg-yellow-500 transition-colors"
                            disabled={selectedTickets[ticket._id] >= ticket.quantity}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              {tickets.length > 0 && (
                <div className="mt-6 space-y-4">
                  <div className="flex justify-between items-center text-xl font-semibold">
                    <span>Total</span>
                    <span>₦{total.toLocaleString()}</span>
                  </div>
                  
                  <button
                    onClick={handleProceed}
                    className="w-full py-4 bg-yellow-400 hover:bg-yellow-500 transition-colors rounded-full font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                  >
                    Proceed
                  </button>
                  
                  {error && (
                    <p className="text-red-500 text-center">{error}</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TicketSelectionModal;
